import {
  checkFlipValue,
  currencyRateConversion
} from "../../utils/hooks/dataHandler";

class MainComputationCalculator {
  static justArraySum(data, shouldDeduct) {
    if (data)
      return (
        data.reduce((sum, row) => {
          const amount = Math.round(parseFloat(row.amount) || 0);
          return shouldDeduct ? sum - amount : sum + amount;
        }, 0) || 0
      );
    else return 0;
  }

  static justArraySumNoRound(data, shouldDeduct) {
    if (data)
      return (
        data.reduce((sum, row) => {
          const amount = parseFloat(row.amount) || 0;
          return shouldDeduct ? sum - amount : sum + amount;
        }, 0) || 0
      );
    else return 0;
  }

  static calTaxAdjustments(data) {
    if (data)
      return (
        data.reduce((sum, row) => {
          if (row.row_id === "ca-summary-00")
            return sum + Math.round(parseFloat(row.amount) || 0);

          const amount = Math.abs(Math.round(parseFloat(row.amount) || 0));
          let shouldDeduct = row.taxAdjustment === "Deduct";

          return shouldDeduct ? sum - amount : sum + amount;
        }, 0) || 0
      );
    else return 0;
  }

  static convertChargeableIncomeToSgd(
    chargeableIncome,
    generalExchangeRate,
    unutilisedRows = []
  ) {
    const parsedGeneralRate = parseFloat(generalExchangeRate);
    const generalRate = parsedGeneralRate > 0 ? parsedGeneralRate : 1;
    const income = parseFloat(chargeableIncome) || 0;

    return unutilisedRows.reduce((sgdIncome, row) => {
      if (row?.isForfeited) return sgdIncome;

      const amount = Math.abs(parseFloat(row?.amount) || 0);
      const rowRate = parseFloat(row?.exchangeRate);
      const historicalRate = rowRate > 0 ? rowRate : generalRate;

      return (
        sgdIncome +
        currencyRateConversion(amount, generalRate, "SGD") -
        currencyRateConversion(amount, historicalRate, "SGD")
      );
    }, currencyRateConversion(income, generalRate, "SGD"));
  }

  static calPartialTaxExemption(isStartup, adjustedProfitAfterDonations) {
    const chargeableIncome = parseFloat(adjustedProfitAfterDonations) || 0;

    // Calculate the first and second exempt amounts
    const firstExemptAmount = isStartup
      ? Math.min(chargeableIncome, 100000) * 0.75
      : Math.min(chargeableIncome, 10000) * 0.75;
    const secondExemptAmount = isStartup
      ? Math.min(Math.max(chargeableIncome - 100000, 0), 100000) * 0.5
      : Math.min(Math.max(chargeableIncome - 10000, 0), 190000) * 0.5;

    return [
      firstExemptAmount + secondExemptAmount,
      firstExemptAmount,
      secondExemptAmount
    ];
  }

  static calAdjustedProfitBeforeCapital(
    netProfitBeforeTax,
    mainComputationData,
    flipSignMode
  ) {
    let taxAdjustment = MainComputationCalculator.calTaxAdjustments(
      mainComputationData["Tax Adjustments"]
    );
    let furtherDeduction = MainComputationCalculator.justArraySum(
      mainComputationData["Further Deduction"],
      checkFlipValue(flipSignMode["Further Deduction"])
    );
    let otherIncome = MainComputationCalculator.justArraySum(
      mainComputationData["Other Source Income"],
      checkFlipValue(flipSignMode["Other Source Income"])
    );

    return netProfitBeforeTax + taxAdjustment + furtherDeduction + otherIncome;
  }

  static calUtilisedCYCA(mainComputationData, flipSignMode) {
    return MainComputationCalculator.justArraySum(
      mainComputationData["Current Year Capital Allowance"],
      checkFlipValue(flipSignMode["Current Year Capital Allowance"])
    );
  }

  static calAdjustedProfitAfterCapital(
    adjustedProfitBeforeCapital,
    calUtilisedCYCA,
    mainComputationData
  ) {
    let adjustedProfitAfterCapital = 0;
    let mediatorAPAC = 0;
    if (adjustedProfitBeforeCapital > 0) {
      adjustedProfitAfterCapital =
        adjustedProfitBeforeCapital +
        MainComputationCalculator.justArraySum(
          mainComputationData["Current Year Capital Allowance"],
          calUtilisedCYCA
        );
      if (adjustedProfitAfterCapital > 0) {
        mediatorAPAC = 0;
      } else {
        mediatorAPAC = adjustedProfitAfterCapital;
        adjustedProfitAfterCapital = 0;
      }
    }

    return [adjustedProfitAfterCapital, mediatorAPAC];
  }

  static calTaxPayable(
    mainComputationData,
    adjustedProfitAfterDonations,
    partialTaxExemption
  ) {
    const chargeableIncome = parseFloat(adjustedProfitAfterDonations) || 0;

    // Calculate the taxable income and tax payable
    const taxableIncome = chargeableIncome - partialTaxExemption;
    const taxPayable = taxableIncome * 0.17;

    // Calculate the capped amount
    const cappedAmount = Math.min(taxPayable * 0.25, 15000);

    const netTaxPayable = taxPayable - cappedAmount;

    const taxPreviouslyAssessed =
      parseFloat(mainComputationData["Tax Previously Assessed"][0]?.amount) ||
      0;
    const additionalTaxPayable = netTaxPayable - taxPreviouslyAssessed;

    return [netTaxPayable, additionalTaxPayable];
  }

  static processAddition(floatingTotal, additionCF, resetVarsCallback) {
    if (floatingTotal > 0) {
      floatingTotal += additionCF;
      if (floatingTotal > 0) {
        additionCF = 0;
        return { floatingTotal, additionCF, resetVars: false };
      } else {
        resetVarsCallback();
      }
    }
    return null;
  }
}

export default MainComputationCalculator;
